import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, throwError, from } from 'rxjs';
import { map, catchError, switchMap, finalize, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { UserModel } from '../models/user.model';
import { AuthHTTPService } from './auth-http/auth-http.service';
import { ResultModel } from 'src/app/models/result.model';
import { RegisterResponseModel } from '../models/register-response.model';
import { ChangePasswordResponseModel } from 'src/app/models/change-password/change-password-response.model';
import { ChangePasswordModel } from 'src/app/models/change-password/change-password.model';
import { HttpHeaders } from '@angular/common/http';
import { ResultResendActivationCodeModel } from 'src/app/models/result-resend-activation-code.model';
import { ForgotChangePasswordModel } from 'src/app/models/forgot-change-password/change-password.model';
import { ForgotChangePasswordResponseModel } from 'src/app/models/forgot-change-password/forgot-change-password-response.model';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    // Initialize user from localStorage if available
    this.initializeUserFromStorage();
  }

  private initializeUserFromStorage(): void {
    const auth = this.getAuthFromLocalStorage();
    if (auth && auth.accessToken) {
      // Set the user immediately from localStorage to prevent guard issues
      this.currentUserSubject.next(auth);
    }
  }

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  login(username: string, password: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(username, password)
      .pipe(
        map((userModel: UserModel) => {
          this.setAuthFromLocalStorage(userModel);
          return userModel;
        }),
        switchMap(() => this.getUserByToken()),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  // need create new user
  registerWithImage(formData: FormData): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createNewUser(formData).pipe(
      map((registerResponseModel: RegisterResponseModel) => {
        //this.isLoadingSubject.next(false);
        return registerResponseModel;
      }),
      catchError((err) => {
        console.error('err', err);
        const registerResponseModel = new RegisterResponseModel();
        registerResponseModel.success = false;
        registerResponseModel.errors = new Array();
        const error = 'Terdapat ralat semasa pendaftaran. Sila cuba lagi dan hubungi pihak LGM jika berterusan';
        registerResponseModel.errors[0] = error;
        return of(registerResponseModel);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user
  changePassword(changePasswordModel: ChangePasswordModel): Observable<ChangePasswordResponseModel | any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.changePassword(changePasswordModel).pipe(
      map((changePasswordResponseModel: ChangePasswordResponseModel) => {
        //this.isLoadingSubject.next(false);
        return changePasswordResponseModel;
      }),
      // user need to verify email first
      //switchMap(() => this.loginWithOpenId(user.nric, user.password)),
      catchError((err) => {
        //console.error('err', err);
        return of('Terdapat ralat semasa menukar kata laluan. Sila cuba lagi dan hubungi pihak LGM jika berterusan');
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

   // need create new user
   resetPassword(changePasswordModel: ForgotChangePasswordModel): Observable<ForgotChangePasswordResponseModel | any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.resetPassword(changePasswordModel).pipe(
      map((changePasswordResponseModel: ForgotChangePasswordResponseModel) => {
        //this.isLoadingSubject.next(false);
        return changePasswordResponseModel;
      }),
      // user need to verify email first
      //switchMap(() => this.loginWithOpenId(user.nric, user.password)),
      catchError((err) => {
        //console.error('err', err);
        return of('Terdapat ralat semasa set semula kata laluan. Sila cuba lagi dan hubungi pihak LGM jika berterusan');
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.accessToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getAccessTokenUsingRefreshToken(): Observable<string> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of('');
    }
    return from(auth.refreshToken).pipe(
      switchMap(refreshToken => {
        return this.authHttpService.requestRefreshToken(refreshToken);
      }),
      map(response => response.access_token),
      // tap(accessToken => this.storage.set(this.ACCESS_TOKEN, accessToken))
    );
  }

  private getAuthFromLocalStorage(): UserModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }
      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private setAuthFromLocalStorage(userModel: UserModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (userModel && userModel.accessToken) {
      // Preserve biometric credentials before clearing
      const biometricCredentials = localStorage.getItem('biometric_credentials');
      const rememberMeUser = localStorage.getItem('remember_me_user');

      localStorage.clear();

      // Restore biometric credentials after clearing
      if (biometricCredentials) {
        localStorage.setItem('biometric_credentials', biometricCredentials);
      }
      if (rememberMeUser) {
        localStorage.setItem('remember_me_user', rememberMeUser);
      }

      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(userModel));
      localStorage.setItem('username', userModel.username);
      localStorage.setItem('userId', userModel.id);
      localStorage.setItem('fullname', userModel.name);
      localStorage.setItem('email', userModel.email);
      localStorage.setItem('nric', userModel.nric);      // New user who does not have Patg account does not have memberId
      localStorage.setItem('accountStatus', userModel.accountStatus);
      localStorage.setItem('phoneNo', userModel.phoneNumber);
      localStorage.setItem('city', userModel.city);
     // console.log("accs :" + userModel.accountStatus);
      if (userModel.memberId !== undefined) {
        localStorage.setItem('memberId', userModel.memberId.toString());
      }
      else
        localStorage.setItem('memberId', "0");

      if (userModel.officeId !== undefined) {
        localStorage.setItem('officeId', userModel.officeId.toString());
      }
      else{
        localStorage.setItem('officeId', '0');
      }


      return true;
    }
    return false;
  }

  logout() {
    this.clearStorage();
    let regisration = localStorage.getItem('registration');
    if (regisration) {
      this.router.navigate(['/auth/registration']);
    }
    else {
      // this.router.navigate(['/auth/login']);
      this.router.navigate(['/auth/welcome']);
    }
    // set this to undefined so that it will not login again (see code in login constructor)
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
  }

  resendActivationCode(username: string, channel?: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.requestResendActivationCode(username, channel)
    .pipe(
      map((requestActivationCodeResp: ResultResendActivationCodeModel) => {
        return requestActivationCodeResp;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  sendActivationCode(username: string, phoneNumber: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.requestResendActivationCode(username)
    .pipe(
      map((requestActivationCodeResp: ResultModel) => {
        return requestActivationCodeResp;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  verifyActivationCode(code: string, phoneNumber: string, username: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.verifyActivationCode(code, phoneNumber, username)
    .pipe(
      map((requestActivationCodeResp: ResultModel) => {
        return requestActivationCodeResp;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  verifyToken(code: string, phoneNumber: string, username: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.verifyToken(code, phoneNumber, username)
    .pipe(
      map((requestActivationCodeResp: ResultModel) => {
        return requestActivationCodeResp;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  /**
   * Request forgot password by username
   */
  forgotPassword(email: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(
        map((resultModel: ResultModel) => {
          return resultModel;
        }),
        catchError((err) => {
          console.error('err', err);
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  forgotPasswordByPhoneNumber(username: string, channel?: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPasswordByPhoneNumber(username, channel)
      .pipe(
        map((resultModel: ResultModel) => {
          return resultModel;
        }),
        catchError((err) => {
          console.error('err', err);
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  clearStorage() {
    // Preserve biometric credentials on logout so user can use quick login next time
    const biometricCredentials = localStorage.getItem('biometric_credentials');
    const rememberMeUser = localStorage.getItem('remember_me_user');

    localStorage.removeItem(this.authLocalStorageToken);
    // localStorage.removeItem('username');
    // localStorage.removeItem('userId');
    // localStorage.removeItem('memberId');
    // localStorage.removeItem('accountStatus');
    // localStorage.removeItem('fullname');
    // localStorage.removeItem('nric');
    // localStorage.removeItem('member');
    // localStorage.removeItem('patgRegStatus');
    // localStorage.removeItem('profileImage');
    // localStorage.removeItem('phoneNo');
    // localStorage.removeItem('email');
    localStorage.clear();

    // Restore biometric credentials after clearing
    if (biometricCredentials) {
      localStorage.setItem('biometric_credentials', biometricCredentials);
    }
    if (rememberMeUser) {
      localStorage.setItem('remember_me_user', rememberMeUser);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
